import { useState, useEffect } from 'react';

export default function QuoteFormSection() {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    servicio: '',
    ubicacion: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  useEffect(() => {
    // Verificar si Calendly ya está cargado
    if (window.Calendly) {
      setCalendlyLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Cuando se muestra el calendario, inicializar el widget
    if (showCalendar && calendlyLoaded && window.Calendly) {
      // Pequeño delay para asegurar que el DOM está listo
      setTimeout(() => {
        window.Calendly.initInlineWidget({
          url: 'https://calendly.com/hormipremium/30min',
          parentElement: document.querySelector('.calendly-inline-widget'),
          prefill: {
            name: formData.nombre,
            email: formData.email,
          },
        });
      }, 100);
    }
  }, [showCalendar, calendlyLoaded, formData.nombre, formData.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/send-quote-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.nombre,
          email: formData.email,
          phone: formData.telefono,
          service: formData.servicio,
          message: `Ubicación: ${formData.ubicacion}\n\n${formData.mensaje}`
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        // Mostrar el calendario después de enviar el formulario
        setTimeout(() => {
          setShowCalendar(true);
        }, 1500);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSkipCalendar = () => {
    // Resetear el formulario y volver al inicio
    setFormData({
      nombre: '',
      telefono: '',
      email: '',
      servicio: '',
      ubicacion: '',
      mensaje: ''
    });
    setShowCalendar(false);
    setSubmitStatus('idle');
  };

  return (
    <section id="presupuesto" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">
            Solicita un Presupuesto Gratuito
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {!showCalendar 
              ? 'Completa el formulario y luego agenda tu cita para hablar sobre tu proyecto'
              : 'Ahora selecciona una fecha y hora para tu cita de 30 minutos'
            }
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {!showCalendar ? (
            // Formulario de Contacto
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-800 mb-2">
                  <i className="ri-mail-line mr-2 text-teal-500"></i>
                  Paso 1: Cuéntanos sobre tu Proyecto
                </h3>
                <p className="text-slate-600">
                  Completa el formulario y después podrás agendar tu cita
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-semibold text-slate-700 mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm"
                    placeholder="Juan Pérez"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-semibold text-slate-700 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm"
                    placeholder="+34 600 000 000"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="servicio" className="block text-sm font-semibold text-slate-700 mb-2">
                    Servicio de Interés *
                  </label>
                  <select
                    id="servicio"
                    name="servicio"
                    value={formData.servicio}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm cursor-pointer"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="Hormigón Impreso">Hormigón Impreso</option>
                    <option value="Hormigón Pulido">Hormigón Pulido</option>
                    <option value="Hormigón Desactivado">Hormigón Desactivado</option>
                    <option value="Piscinas de Arelite">Piscinas de Arelite</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="ubicacion" className="block text-sm font-semibold text-slate-700 mb-2">
                    Ubicación del Proyecto
                  </label>
                  <input
                    type="text"
                    id="ubicacion"
                    name="ubicacion"
                    value={formData.ubicacion}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition text-sm"
                    placeholder="Madrid, Barcelona, etc."
                  />
                </div>

                <div>
                  <label htmlFor="mensaje" className="block text-sm font-semibold text-slate-700 mb-2">
                    Mensaje / Detalles del Proyecto
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition resize-none text-sm"
                    placeholder="Cuéntanos sobre tu proyecto... (máx 500 caracteres)"
                  ></textarea>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.mensaje.length}/500 caracteres
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-check-line mr-2"></i>
                    ¡Perfecto! Ahora agenda tu cita...
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    Ha ocurrido un error. Por favor intenta de nuevo o contáctanos por WhatsApp.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-4 px-6 rounded-lg transition duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2"></i>
                      Continuar al Calendario
                    </>
                  )}
                </button>
              </form>
            </div>
          ) : (
            // Calendario de Calendly
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-800 mb-2">
                  <i className="ri-calendar-line mr-2 text-teal-500"></i>
                  Paso 2: Agenda tu Cita
                </h3>
                <p className="text-slate-600 mb-4">
                  Selecciona el día y hora que mejor te convenga para hablar sobre tu proyecto
                </p>
                <div className="flex items-start gap-3 text-sm text-slate-600 bg-slate-50 p-4 rounded-lg">
                  <i className="ri-information-line text-teal-500 text-lg mt-0.5"></i>
                  <div>
                    <p className="font-semibold mb-1">Detalles de la cita:</p>
                    <ul className="space-y-1">
                      <li>• Duración: 30 minutos</li>
                      <li>• Tipo: Llamada o videoconferencia</li>
                      <li>• Confirmación enviada automáticamente por email</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div 
                className="calendly-inline-widget" 
                style={{ minWidth: '320px', height: '700px', position: 'relative' }}>
                {!calendlyLoaded && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <i className="ri-loader-4-line animate-spin text-4xl text-teal-500 mb-4"></i>
                      <p className="text-slate-600">Cargando calendario...</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={handleSkipCalendar}
                  className="text-slate-600 hover:text-slate-800 text-sm font-medium transition whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Agendar más tarde
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}